import React, { useEffect, useMemo, useState } from "react";
import { getMaterialLabel, MATERIAL_DATABASE } from "../../data/machiningMaterials";
import MaterialSelector from "../MaterialSelector";

const TOOL_MATERIALS = {
  carbide: {
    label: "Carbide",
    modulusGPa: 600,
    wearResistance: 1.25,
  },
  hss: {
    label: "HSS",
    modulusGPa: 210,
    wearResistance: 1.0,
  },
  cobalt: {
    label: "Cobalt",
    modulusGPa: 230,
    wearResistance: 1.08,
  },
};

const createCutId = () =>
  `cut_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const createCut = (id, radialDepth, axialDepth, cutLength, passCount) => ({
  id,
  radialDepth,
  axialDepth,
  cutLength,
  passCount,
});

const DEFAULT_PARAMS = {
  units: "inch",
  toolMaterial: "carbide",
  workpieceMaterial: "mild_steel",
  toolDiameter: 0.5,
  stickoutLength: 1.5,
};

const DEFAULT_CUTS = [createCut("cut_1", 0.05, 0.25, 1.0, 1)];

const getWorkpieceMaterial = (materialKey) => {
  const material = MATERIAL_DATABASE[materialKey];
  if (!material) return null;

  const hardness = material.hardnessHB || 180;
  const machinability = material.machinability || 50;
  return {
    label: material.label,
    kcMpa: Math.round(900 + hardness * 4 + Math.max(0, 70 - machinability) * 18),
    wearFactor: Math.max(0.7, Math.min(1.8, 70 / machinability)),
  };
};

const roundTo = (value, decimals = 4) => {
  if (!Number.isFinite(value)) return 0;
  return Number(value.toFixed(decimals));
};

const convertLengthToMeters = (value, units) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return units === "metric" ? numeric / 1000 : numeric * 0.0254;
};

const convertLengthToMillimeters = (value, units) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return units === "metric" ? numeric : numeric * 25.4;
};

const formatLengthForDisplay = (meters, units) => {
  if (units === "metric") {
    return {
      primary: `${roundTo(meters * 1000, 4)} mm`,
      secondary: `${roundTo(meters * 1000000, 1)} µm`,
    };
  }

  return {
    primary: `${roundTo(meters / 0.0254, 6)} in`,
    secondary: `${roundTo(meters * 1000, 4)} mm`,
  };
};

const calculateToolDeflectionFromCuts = ({ params, cuts }) => {
  const errors = {};
  const warnings = [];

  const units = params.units || "inch";
  const toolMaterial = TOOL_MATERIALS[params.toolMaterial];
  const workpieceMaterial = getWorkpieceMaterial(params.workpieceMaterial);

  const toolDiameterInput = Number(params.toolDiameter);
  const stickoutInput = Number(params.stickoutLength);

  if (!toolMaterial) {
    errors.toolMaterial = "Invalid tool material.";
  }

  if (!workpieceMaterial) {
    errors.workpieceMaterial = "Invalid workpiece material.";
  }

  if (params.toolDiameter === "" || params.toolDiameter === null || params.toolDiameter === undefined) {
    errors.toolDiameter = "More data required";
  } else if (!toolDiameterInput || toolDiameterInput <= 0) {
    errors.toolDiameter = "Tool diameter must be greater than zero.";
  }

  if (params.stickoutLength === "" || params.stickoutLength === null || params.stickoutLength === undefined) {
    errors.stickoutLength = "More data required";
  } else if (!stickoutInput || stickoutInput <= 0) {
    errors.stickoutLength = "Stickout length must be greater than zero.";
  }

  if (!Array.isArray(cuts) || cuts.length === 0) {
    errors.cuts = "At least one cut is required.";
  }

  const normalizedCuts = (cuts || []).map((cut, index) => {
    const radialDepth = Number(cut.radialDepth);
    const axialDepth = Number(cut.axialDepth);
    const cutLength = Number(cut.cutLength);
    const passCount = Number(cut.passCount);

    if (cut.radialDepth === "" || cut.radialDepth === null || cut.radialDepth === undefined) {
      errors[`cut_${index}_radialDepth`] = "More data required";
    } else if (!radialDepth || radialDepth <= 0) {
      errors[`cut_${index}_radialDepth`] = `Cut ${index + 1} radial depth must be greater than zero.`;
    }

    if (cut.axialDepth === "" || cut.axialDepth === null || cut.axialDepth === undefined) {
      errors[`cut_${index}_axialDepth`] = "More data required";
    } else if (!axialDepth || axialDepth <= 0) {
      errors[`cut_${index}_axialDepth`] = `Cut ${index + 1} axial depth must be greater than zero.`;
    }

    if (cut.cutLength === "" || cut.cutLength === null || cut.cutLength === undefined) {
      errors[`cut_${index}_cutLength`] = "More data required";
    } else if (!cutLength || cutLength <= 0) {
      errors[`cut_${index}_cutLength`] = `Cut ${index + 1} cut length must be greater than zero.`;
    }

    if (cut.passCount === "" || cut.passCount === null || cut.passCount === undefined) {
      errors[`cut_${index}_passCount`] = "More data required";
    } else if (!passCount || passCount <= 0) {
      errors[`cut_${index}_passCount`] = `Cut ${index + 1} pass count must be greater than zero.`;
    }

    return {
      ...cut,
      radialDepth,
      axialDepth,
      cutLength,
      passCount,
    };
  });

  if (Object.keys(errors).length > 0) {
    return { errors, confidence: "Invalid" };
  }

  const toolDiameterMeters = convertLengthToMeters(toolDiameterInput, units);
  const stickoutMeters = convertLengthToMeters(stickoutInput, units);
  const toolDiameterMM = convertLengthToMillimeters(toolDiameterInput, units);
  const elasticModulusPa = toolMaterial.modulusGPa * 1e9;
  const momentOfInertia = (Math.PI * Math.pow(toolDiameterMeters, 4)) / 64;
  const kc = workpieceMaterial.kcMpa;

  const lengthToDiameterRatio = stickoutMeters / toolDiameterMeters;
  const perCutResults = [];

  let cumulativeRemovedVolumeMM3 = 0;
  let maxDeflectionMeters = 0;
  let totalRemovedVolumeMM3 = 0;
  let totalPasses = 0;

  normalizedCuts.forEach((cut, index) => {
    const radialDepthMM = convertLengthToMillimeters(cut.radialDepth, units);
    const axialDepthMM = convertLengthToMillimeters(cut.axialDepth, units);
    const cutLengthMM = convertLengthToMillimeters(cut.cutLength, units);

    const radialEngagementRatio = Math.min(
      radialDepthMM / Math.max(toolDiameterMM, 0.000001),
      1
    );

    const perPassRemovedVolumeMM3 =
      radialDepthMM * axialDepthMM * cutLengthMM;
    const cutRemovedVolumeMM3 = perPassRemovedVolumeMM3 * cut.passCount;

    const engagementFactor = 0.015 + 0.085 * radialEngagementRatio;
    const baseForceN =
      kc * radialDepthMM * axialDepthMM * engagementFactor;

    const progressiveWearMultiplier =
      1 +
      ((cumulativeRemovedVolumeMM3 + cutRemovedVolumeMM3) / 100000) *
        (workpieceMaterial.wearFactor / toolMaterial.wearResistance);

    const adjustedForceN = baseForceN * progressiveWearMultiplier;

    const tipDeflectionMeters =
      (adjustedForceN * Math.pow(stickoutMeters, 3)) /
      (3 * elasticModulusPa * momentOfInertia);

    const displayDeflection = formatLengthForDisplay(
      tipDeflectionMeters,
      units
    );

    cumulativeRemovedVolumeMM3 += cutRemovedVolumeMM3;
    totalRemovedVolumeMM3 += cutRemovedVolumeMM3;
    totalPasses += cut.passCount;
    maxDeflectionMeters = Math.max(maxDeflectionMeters, tipDeflectionMeters);

    perCutResults.push({
      index: index + 1,
      radialDepth: cut.radialDepth,
      axialDepth: cut.axialDepth,
      cutLength: cut.cutLength,
      passCount: cut.passCount,
      radialDepthMM: roundTo(radialDepthMM, 4),
      axialDepthMM: roundTo(axialDepthMM, 4),
      cutLengthMM: roundTo(cutLengthMM, 4),
      removedVolumeMM3: roundTo(cutRemovedVolumeMM3, 3),
      baseForceN: roundTo(baseForceN, 2),
      adjustedForceN: roundTo(adjustedForceN, 2),
      progressiveWearMultiplier: roundTo(progressiveWearMultiplier, 4),
      deflectionMeters: tipDeflectionMeters,
      deflectionPrimary: displayDeflection.primary,
      deflectionSecondary: displayDeflection.secondary,
    });
  });

  const finalCut = perCutResults[perCutResults.length - 1];
  const maxDeflectionDisplay = formatLengthForDisplay(maxDeflectionMeters, units);

  if (lengthToDiameterRatio > 5) {
    warnings.push("High length-to-diameter ratio. Deflection rises quickly as stickout increases.");
  }

  if (lengthToDiameterRatio > 7) {
    warnings.push("Very slender setup. Chatter and size error risk are high.");
  }

  if (perCutResults.some((cut) => cut.adjustedForceN > 600)) {
    warnings.push("One or more cuts generate high estimated cutting load. Consider reducing engagement or stickout.");
  }

  if (perCutResults.some((cut) => cut.progressiveWearMultiplier > 1.2)) {
    warnings.push("Cumulative cut volume is increasing estimated load over time. Later passes may deflect more.");
  }

  if (units === "inch" && maxDeflectionMeters / 0.0254 > 0.001) {
    warnings.push("Maximum estimated deflection exceeds 0.001 in.");
  }

  if (units === "metric" && maxDeflectionMeters * 1000 > 0.025) {
    warnings.push("Maximum estimated deflection exceeds 0.025 mm.");
  }

  return {
    perCutResults,
    maxDeflectionPrimary: maxDeflectionDisplay.primary,
    maxDeflectionSecondary: maxDeflectionDisplay.secondary,
    finalCutDeflectionPrimary: finalCut?.deflectionPrimary || "0",
    finalCutDeflectionSecondary: finalCut?.deflectionSecondary || "0",
    totalRemovedVolumeMM3: roundTo(totalRemovedVolumeMM3, 3),
    totalPasses,
    lengthToDiameterRatio: roundTo(lengthToDiameterRatio, 3),
    elasticModulusGPa: toolMaterial.modulusGPa,
    kcMpa: workpieceMaterial.kcMpa,
    momentOfInertiaMM4: roundTo(momentOfInertia * 1e12, 6),
    warnings,
    confidence: warnings.length === 0 ? "High" : "Medium",
  };
};

const ToolDeflectionCalculator = ({ onSave, savingCalculation }) => {
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [cuts, setCuts] = useState(DEFAULT_CUTS);
  const [results, setResults] = useState(null);

  useEffect(() => {
    setResults(calculateToolDeflectionFromCuts({ params, cuts }));
  }, [params, cuts]);

  const hasErrors = results?.errors && Object.keys(results.errors).length > 0;

  const unitLabels = useMemo(() => {
    return params.units === "metric"
      ? {
          diameter: "Tool Diameter (mm)",
          stickout: "Tool Stickout (mm)",
          radialDepth: "X/Y Depth / Radial Engagement (mm)",
          axialDepth: "Z Depth / Axial Depth (mm)",
          cutLength: "Length of Cut (mm)",
        }
      : {
          diameter: 'Tool Diameter (in)',
          stickout: 'Tool Stickout (in)',
          radialDepth: 'X/Y Depth / Radial Engagement (in)',
          axialDepth: 'Z Depth / Axial Depth (in)',
          cutLength: 'Length of Cut (in)',
        };
  }, [params.units]);

  const handleParamChange = (field, value) => {
    setParams((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleParamNumberChange = (field, value) => {
    setParams((prev) => ({
      ...prev,
      [field]: value === "" ? "" : parseFloat(value),
    }));
  };

  const handleCutChange = (cutId, field, value) => {
    setCuts((prev) =>
      prev.map((cut) =>
        cut.id === cutId
          ? {
          ...cut,
          [field]:
              value === ""
                ? ""
                : field === "passCount"
                  ? parseInt(value, 10)
                  : parseFloat(value),
        }
      : cut
    )
  );
  };

  const addCut = () => {
    setCuts((prev) => [
      ...prev,
      createCut(createCutId(), 0.05, 0.25, 1.0, 1),
    ]);
  };

  const removeCut = (cutId) => {
    setCuts((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((cut) => cut.id !== cutId);
    });
  };

  const resetForm = () => {
    setParams(DEFAULT_PARAMS);
    setCuts(DEFAULT_CUTS);
  };

  const copyToClipboard = () => {
    if (!results || hasErrors) return;

    const text = [
      "ManuCalc - Tool Deflection Calculator",
      `Units: ${params.units === "metric" ? "Metric" : "Imperial"}`,
      `Tool Material: ${TOOL_MATERIALS[params.toolMaterial]?.label || params.toolMaterial}`,
      `Workpiece Material: ${getMaterialLabel(params.workpieceMaterial)}`,
      `Tool Diameter: ${params.toolDiameter}`,
      `Tool Stickout: ${params.stickoutLength}`,
      `Maximum Deflection: ${results.maxDeflectionPrimary} (${results.maxDeflectionSecondary})`,
      `Final Cut Deflection: ${results.finalCutDeflectionPrimary} (${results.finalCutDeflectionSecondary})`,
      `L/D Ratio: ${results.lengthToDiameterRatio}`,
      `Total Passes: ${results.totalPasses}`,
      `Total Removed Volume: ${results.totalRemovedVolumeMM3} mm³`,
      ...results.perCutResults.map(
        (cut) =>
          `Cut ${cut.index}: ae=${cut.radialDepth}, ap=${cut.axialDepth}, L=${cut.cutLength}, passes=${cut.passCount}, force=${cut.adjustedForceN} N, deflection=${cut.deflectionPrimary}`
      ),
    ].join("\n");

    navigator.clipboard.writeText(text);
  };

  const handleSave = () => {
    if (!results || hasErrors || !onSave) return;

    onSave({
      summary: `${getMaterialLabel(params.workpieceMaterial)} | Max Deflection ${results.maxDeflectionPrimary}`,
      input: {
        units: params.units,
        toolMaterial:
          TOOL_MATERIALS[params.toolMaterial]?.label || params.toolMaterial,
        workpieceMaterial: getMaterialLabel(params.workpieceMaterial),
        toolDiameter: params.toolDiameter,
        stickoutLength: params.stickoutLength,
        cuts: cuts.map((cut, index) => ({
          position: index + 1,
          radialDepth: cut.radialDepth,
          axialDepth: cut.axialDepth,
          cutLength: cut.cutLength,
          passCount: cut.passCount,
        })),
      },
      results: {
        maxDeflectionPrimary: results.maxDeflectionPrimary,
        finalCutDeflectionPrimary: results.finalCutDeflectionPrimary,
        totalRemovedVolumeMM3: results.totalRemovedVolumeMM3,
        totalPasses: results.totalPasses,
        lengthToDiameterRatio: results.lengthToDiameterRatio,
        perCutResults: results.perCutResults,
      },
      warnings: Array.isArray(results.warnings) ? results.warnings : [],
      confidence: results.confidence || "N/A",
    });
  };

  return (
    <div
      className="calculator-container"
      style={{
        background: "var(--bg-surface)",
        color: "var(--text-secondary)",
        border: "1px solid var(--border-color)",
        borderRadius: "12px",
        padding: "24px",
      }}
    >
      <div className="calculator-header" style={{ marginBottom: "24px" }}>
        <h1 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
          📉 Tool Deflection Calculator
        </h1>
        <p style={{ color: "var(--text-muted)", margin: 0 }}>
          Estimate cut-by-cut deflection using tool stiffness, workpiece
          material resistance, engagement size, and cumulative cutting volume.
        </p>
      </div>

      <div
        className="calculator-content"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
          gap: "24px",
        }}
      >
        <div
          className="calculator-form"
          style={{
            background: "var(--bg-surface-alt)",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            padding: "20px",
          }}
        >
          <div
            style={{
              marginBottom: "18px",
              padding: "14px",
              borderRadius: "12px",
              background: "rgba(59,130,246,0.08)",
              border: "1px solid rgba(59,130,246,0.18)",
            }}
          >
            <div
              style={{
                color: "var(--text-primary)",
                fontWeight: 700,
                marginBottom: "6px",
              }}
            >
              Global Setup
            </div>
            <div style={{ color: "var(--text-muted)", fontSize: "14px", lineHeight: 1.6 }}>
              Tool stiffness stays global. Each cut below can have its own radial
              depth, axial depth, cut length, and pass count.
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: "20px" }}>
            <label
              htmlFor="units"
              style={{
                display: "block",
                marginBottom: "8px",
                color: "var(--text-primary)",
                fontWeight: 600,
              }}
            >
              Units
            </label>
            <select
              id="units"
              value={params.units}
              onChange={(e) => handleParamChange("units", e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--border-color)",
                background: "var(--bg-surface)",
                color: "var(--text-primary)",
              }}
            >
              <option value="inch">Imperial</option>
              <option value="metric">Metric</option>
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: "20px" }}>
            <label
              htmlFor="toolMaterial"
              style={{
                display: "block",
                marginBottom: "8px",
                color: "var(--text-primary)",
                fontWeight: 600,
              }}
            >
              Tool Material
            </label>
            <select
              id="toolMaterial"
              value={params.toolMaterial}
              onChange={(e) => handleParamChange("toolMaterial", e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--border-color)",
                background: "var(--bg-surface)",
                color: "var(--text-primary)",
              }}
            >
              {Object.entries(TOOL_MATERIALS).map(([key, material]) => (
                <option key={key} value={key}>
                  {material.label}
                </option>
              ))}
            </select>
          </div>

          <MaterialSelector
            value={params.workpieceMaterial}
            onChange={(value) => handleParamChange("workpieceMaterial", value)}
            error={results?.errors?.workpieceMaterial}
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: "12px",
              marginBottom: "20px",
            }}
          >
            <div className="form-group">
              <label
                htmlFor="toolDiameter"
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                }}
              >
                {unitLabels.diameter}
              </label>
              <input
                id="toolDiameter"
                type="number"
                step="0.001"
                min="0"
                value={params.toolDiameter}
                onChange={(e) =>
                  handleParamNumberChange("toolDiameter", e.target.value)
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div className="form-group">
              <label
                htmlFor="stickoutLength"
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                }}
              >
                {unitLabels.stickout}
              </label>
              <input
                id="stickoutLength"
                type="number"
                step="0.001"
                min="0"
                value={params.stickoutLength}
                onChange={(e) =>
                  handleParamNumberChange("stickoutLength", e.target.value)
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          <div
            style={{
              marginBottom: "14px",
              marginTop: "24px",
              color: "var(--text-primary)",
              fontWeight: 700,
              fontSize: "15px",
            }}
          >
            Cut Sequence
          </div>

          <div style={{ display: "grid", gap: "14px", marginBottom: "18px" }}>
            {cuts.map((cut, index) => (
              <div
                key={cut.id}
                style={{
                  padding: "14px",
                  borderRadius: "14px",
                  background: "var(--bg-surface)",
                  border: "1px solid var(--border-color)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    alignItems: "center",
                    marginBottom: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                    }}
                  >
                    Cut {index + 1}
                  </div>

                  {cuts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCut(cut.id)}
                      className="btn-secondary"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",
                    gap: "12px",
                  }}
                >
                  <div>
                    <label
                      htmlFor={`radialDepth-${cut.id}`}
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-primary)",
                        fontWeight: 600,
                      }}
                    >
                      {unitLabels.radialDepth}
                    </label>
                    <input
                      id={`radialDepth-${cut.id}`}
                      type="number"
                      step="0.001"
                      min="0"
                      value={cut.radialDepth}
                      onChange={(e) =>
                        handleCutChange(cut.id, "radialDepth", e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid var(--border-color)",
                        background: "var(--bg-surface-alt)",
                        color: "var(--text-primary)",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`axialDepth-${cut.id}`}
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-primary)",
                        fontWeight: 600,
                      }}
                    >
                      {unitLabels.axialDepth}
                    </label>
                    <input
                      id={`axialDepth-${cut.id}`}
                      type="number"
                      step="0.001"
                      min="0"
                      value={cut.axialDepth}
                      onChange={(e) =>
                        handleCutChange(cut.id, "axialDepth", e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid var(--border-color)",
                        background: "var(--bg-surface-alt)",
                        color: "var(--text-primary)",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`cutLength-${cut.id}`}
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-primary)",
                        fontWeight: 600,
                      }}
                    >
                      {unitLabels.cutLength}
                    </label>
                    <input
                      id={`cutLength-${cut.id}`}
                      type="number"
                      step="0.001"
                      min="0"
                      value={cut.cutLength}
                      onChange={(e) =>
                        handleCutChange(cut.id, "cutLength", e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid var(--border-color)",
                        background: "var(--bg-surface-alt)",
                        color: "var(--text-primary)",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`passCount-${cut.id}`}
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "var(--text-primary)",
                        fontWeight: 600,
                      }}
                    >
                      Pass Count
                    </label>
                    <input
                      id={`passCount-${cut.id}`}
                      type="number"
                      step="1"
                      min="1"
                      value={cut.passCount}
                      onChange={(e) =>
                        handleCutChange(cut.id, "passCount", e.target.value)
                      }
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid var(--border-color)",
                        background: "var(--bg-surface-alt)",
                        color: "var(--text-primary)",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div
            className="form-actions"
            style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
          >
            <button onClick={addCut} className="btn-primary" type="button">
              + Add Cut
            </button>
            <button onClick={resetForm} className="btn-secondary" type="button">
              Reset
            </button>
          </div>
        </div>

        <div
          className="calculator-results"
          style={{
            background: "var(--bg-surface-alt)",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            padding: "20px",
            minHeight: "100%",
          }}
        >
          {results && !hasErrors ? (
            <>
              <div
                className="results-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "12px",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: "20px",
                }}
              >
                <h3 style={{ color: "var(--text-primary)", margin: 0 }}>
                  Results
                </h3>
                <div
                  className="confidence-indicator"
                  style={{ color: "var(--text-secondary)" }}
                >
                  Confidence:{" "}
                  <span
                    style={{ color: "var(--accent)", fontWeight: 700 }}
                  >
                    {results.confidence}
                  </span>
                </div>
              </div>

              <div
                className="results-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "12px",
                  marginBottom: "20px",
                }}
              >
                <div
                  className="result-item"
                  style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "10px",
                    padding: "14px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "var(--text-muted)",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    Max Deflection
                  </label>
                  <span
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    {results.maxDeflectionPrimary}
                  </span>
                  <div
                    style={{
                      marginTop: "6px",
                      color: "var(--text-muted)",
                      fontSize: "13px",
                    }}
                  >
                    {results.maxDeflectionSecondary}
                  </div>
                </div>

                <div
                  className="result-item"
                  style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "10px",
                    padding: "14px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "var(--text-muted)",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    Final Cut Deflection
                  </label>
                  <span
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    {results.finalCutDeflectionPrimary}
                  </span>
                  <div
                    style={{
                      marginTop: "6px",
                      color: "var(--text-muted)",
                      fontSize: "13px",
                    }}
                  >
                    {results.finalCutDeflectionSecondary}
                  </div>
                </div>

                <div
                  className="result-item"
                  style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "10px",
                    padding: "14px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "var(--text-muted)",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    L/D Ratio
                  </label>
                  <span
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    {results.lengthToDiameterRatio}
                  </span>
                </div>

                <div
                  className="result-item"
                  style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "10px",
                    padding: "14px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "var(--text-muted)",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    Total Removed Volume
                  </label>
                  <span
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    {results.totalRemovedVolumeMM3} mm³
                  </span>
                </div>
              </div>

              <div
                style={{
                  background: "var(--bg-surface)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "10px",
                  padding: "16px",
                  marginBottom: "20px",
                }}
              >
                <h4
                  style={{
                    color: "var(--text-primary)",
                    marginTop: 0,
                    marginBottom: "10px",
                  }}
                >
                  Setup Summary
                </h4>
                <div style={{ color: "var(--text-secondary)", lineHeight: 1.7 }}>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      Tool Material:
                    </strong>{" "}
                    {TOOL_MATERIALS[params.toolMaterial]?.label}
                  </div>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      Workpiece Material:
                    </strong>{" "}
                    {getMaterialLabel(params.workpieceMaterial)}
                  </div>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      Tool Modulus:
                    </strong>{" "}
                    {results.elasticModulusGPa} GPa
                  </div>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      Material kc:
                    </strong>{" "}
                    {results.kcMpa} MPa
                  </div>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      Section Inertia:
                    </strong>{" "}
                    {results.momentOfInertiaMM4} mm⁴
                  </div>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      Total Passes:
                    </strong>{" "}
                    {results.totalPasses}
                  </div>
                </div>
              </div>

              <div
                style={{
                  background: "var(--bg-surface)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "10px",
                  padding: "16px",
                  marginBottom: "20px",
                }}
              >
                <h4
                  style={{
                    color: "var(--text-primary)",
                    marginTop: 0,
                    marginBottom: "10px",
                  }}
                >
                  Per-Cut Deflection
                </h4>
                <div style={{ display: "grid", gap: "10px" }}>
                  {results.perCutResults.map((cut) => (
                    <div
                      key={cut.index}
                      style={{
                        padding: "12px",
                        borderRadius: "10px",
                        border: "1px solid var(--border-color)",
                        background: "var(--bg-surface-alt)",
                      }}
                    >
                      <div
                        style={{
                          color: "var(--text-primary)",
                          fontWeight: 700,
                          marginBottom: "6px",
                        }}
                      >
                        Cut {cut.index}
                      </div>
                      <div style={{ lineHeight: 1.65 }}>
                        <div>
                          ae: {cut.radialDepth} | ap: {cut.axialDepth} | L: {cut.cutLength} | passes: {cut.passCount}
                        </div>
                        <div>
                          Estimated load:{" "}
                          <strong style={{ color: "var(--text-primary)" }}>
                            {cut.adjustedForceN} N
                          </strong>{" "}
                          (base {cut.baseForceN} N)
                        </div>
                        <div>
                          Progressive multiplier:{" "}
                          <strong style={{ color: "var(--text-primary)" }}>
                            {cut.progressiveWearMultiplier}×
                          </strong>
                        </div>
                        <div>
                          Deflection:{" "}
                          <strong style={{ color: "var(--text-primary)" }}>
                            {cut.deflectionPrimary}
                          </strong>{" "}
                          ({cut.deflectionSecondary})
                        </div>
                        <div>Removed volume: {cut.removedVolumeMM3} mm³</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {Array.isArray(results.warnings) && results.warnings.length > 0 && (
                <div
                  className="warnings"
                  style={{
                    background: "rgba(245, 158, 11, 0.12)",
                    border: "1px solid rgba(245, 158, 11, 0.35)",
                    borderRadius: "10px",
                    padding: "16px",
                    marginBottom: "20px",
                  }}
                >
                  <h4
                    style={{
                      color: "var(--text-primary)",
                      marginBottom: "10px",
                    }}
                  >
                    ⚠️ Warnings
                  </h4>
                  <ul
                    style={{
                      paddingLeft: "20px",
                      color: "var(--text-secondary)",
                      marginBottom: 0,
                    }}
                  >
                    {results.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div
                className="results-actions"
                style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
              >
                <button
                  onClick={handleSave}
                  className="btn-primary"
                  type="button"
                  disabled={savingCalculation}
                >
                  {savingCalculation ? "Saving..." : "💾 Save Calculation"}
                </button>
                <button
                  onClick={copyToClipboard}
                  className="btn-secondary"
                  type="button"
                >
                  📄 Copy
                </button>
              </div>
            </>
          ) : (
            <div
              style={{
                color: "var(--text-muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "180px",
                textAlign: "center",
              }}
            >
              Enter valid inputs to calculate tool deflection across multiple cuts.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToolDeflectionCalculator;
